import React from 'react';
import { Link } from 'react-router-dom';
import MaskedInput from 'react-text-mask';

import PropTypes from 'prop-types';

import Card from '@/components/common/Card';
import GrayButton from '@/components/common/elements/GrayButton';
import inspectionTypesIcon from '@/components/common/icons/records.svg';
import trashImage from '@/components/common/icons/trash.svg';
import userCircleImage from '@/components/common/icons/user-circle.svg';
import Spinner from '@/components/common/Spinner';
import { USERNAME_MASK } from '@/helpers/mask';

const inputClasses =
  'w-full h-[34.48px] px-[5.75px] border border-[#e3e3e3] rounded-[3px] bg-white !text-[11.5px] font-[\'Open_Sans\'] font-semibold text-[#919191] transition-colors focus:outline-none focus:border-[#4d8fca] placeholder:font-normal placeholder:text-[#b1b1b1] disabled:!bg-[#efefef] disabled:cursor-not-allowed';

const UpdateUserPermissionsComponent = ({
  isBusy,
  isClientUserFormSubmitting,
  isClientUserDeleteConfirmationVisible,
  permissions,
  handleSelectedPermissionsChange,
  handleClientUserDeleteButtonClick,
  handleClientUserFormSubmit,
  handleToggleClientUserDeleteConfirmation,
  intl,
  onChange,
  user,
  cpfMask,
  cpfMaskPlaceholder,
  phoneMask,
  phoneMaskPlaceholder,
  cellphoneMask,
  cellphoneMaskLength,
}) => {
  return (
    <div className="flex flex-col p-[1.5em_1em] gap-[0.6em]">
      {isBusy ? (
        <div className="absolute top-[25%] left-1/2 -translate-x-1/2 -translate-y-1/2">
          <Spinner />
        </div>
      ) : (
        <>
          <Card className="fade-in !m-0">
            {isClientUserDeleteConfirmationVisible ? (
              <div className="py-2 flex items-center justify-between">
                <div className="text-center font-bold text-[#393939]">
                  {intl.formatMessage({ id: 'WANT_TO_DELETE_USER_MESSAGE' })}
                </div>

                <div className="flex justify-center gap-2">
                  <button
                    type="button"
                    onClick={handleToggleClientUserDeleteConfirmation}
                    className="flex items-center justify-center w-[100px] h-9 border border-[#e3e3e3] !rounded-xm !text-[#3c3c3c] font-bold hover:bg-[#f8f8f8] transition-colors !no-underline text-sm"
                  >
                    {intl.formatMessage({ id: 'NO' })}
                  </button>

                  <button
                    type="button"
                    onClick={handleClientUserDeleteButtonClick}
                    className="flex items-center justify-center w-[100px] h-9 border border-[#e3e3e3] !rounded-xm !text-[#3c3c3c] font-bold hover:bg-[#f8f8f8] transition-colors !no-underline text-sm"
                  >
                    {intl.formatMessage({ id: 'YES' })}
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex justify-center p-2">
                <div className="flex justify-center">
                  <button
                    type="button"
                    onClick={handleToggleClientUserDeleteConfirmation}
                    className="flex items-center justify-center w-[100px] h-9 border border-[#e3e3e3] !rounded-xm !text-[#3c3c3c] font-bold hover:bg-[#f8f8f8] transition-colors !no-underline text-sm"
                  >
                    <img src={trashImage} alt={intl.formatMessage({ id: 'DELETE' })} className="h-5 mx-auto" />
                  </button>
                </div>
              </div>
            )}
          </Card>

          <Card className="fade-in !m-0 !mt-[0.6em]">
            <h3 className="flex items-center !text-[1.125em] !font-semibold text-[#5882c1] mb-3 pb-2">
              <div className="flex-shrink-0 w-8 mr-2 flex items-center justify-center grayscale-[0.5]">
                <img src={userCircleImage} alt={intl.formatMessage({ id: 'USER' })} className="h-6" />
              </div>

              {intl.formatMessage({ id: 'BASIC_DATA' })}
            </h3>

            <div className="flex flex-col gap-3 mx-10">
              <div className="flex flex-col">
                <label htmlFor="name" className="block mb-1 text-[10px] uppercase text-[#393939] font-bold">
                  {intl.formatMessage({ id: 'fullName' })}*
                </label>
                <input
                  type="text"
                  name="name"
                  id="name"
                  data-cy="name"
                  required
                  onChange={onChange}
                  value={user?.name || ''}
                  placeholder="ex: Usuario Exemplo"
                  className={inputClasses}
                />
              </div>

              <div className="grid gap-4">
                <div className="flex flex-col">
                  <label htmlFor="cpf" className="block mb-1 text-[10px] uppercase text-[#393939] font-bold">
                    {intl.formatMessage({ id: 'CPF' })}
                  </label>
                  <MaskedInput
                    type="text"
                    name="cpf"
                    id="cpf"
                    data-cy="cpf"
                    onChange={onChange}
                    value={user?.cpf || ''}
                    placeholder={`ex: ${cpfMaskPlaceholder}`}
                    guide={false}
                    mask={cpfMask}
                    className={inputClasses}
                  />
                </div>
              </div>
              <div className="grid gap-4">
                <div className="flex flex-col">
                  <label htmlFor="username" className="block mb-1 text-[10px] uppercase text-[#393939] font-bold">
                    {intl.formatMessage({ id: 'USER' })}*
                  </label>
                  <MaskedInput
                    type="text"
                    name="username"
                    id="username"
                    data-cy="username"
                    required
                    onChange={onChange}
                    disabled
                    value={user?.username || ''}
                    placeholder="ex: usuario_exemplo"
                    guide={false}
                    mask={USERNAME_MASK}
                    className={inputClasses}
                  />
                </div>
              </div>

              <div className="grid gap-4">
                <div className="flex flex-col">
                  <label htmlFor="email" className="block mb-1 text-[10px] uppercase text-[#393939] font-bold">
                    {intl.formatMessage({ id: 'EMAIL' })}*
                  </label>
                  <input
                    type="email"
                    name="email"
                    id="email"
                    data-cy="email"
                    required
                    disabled
                    onChange={onChange}
                    value={user?.email || ''}
                    placeholder="ex: usuario_exemplo@redevistorias.com.br"
                    className={inputClasses}
                  />
                </div>
              </div>
              <div className="grid gap-4">
                <div className="flex flex-col">
                  <label htmlFor="phone" className="block mb-1 text-[10px] uppercase text-[#393939] font-bold">
                    {intl.formatMessage({ id: 'CONTACT' })}
                  </label>
                  <MaskedInput
                    type="text"
                    name="phone"
                    id="phone"
                    data-cy="phone"
                    onChange={onChange}
                    value={user?.phone || ''}
                    placeholder={`ex: ${phoneMaskPlaceholder}`}
                    guide={false}
                    mask={value => (value.length >= cellphoneMaskLength ? cellphoneMask : phoneMask)}
                    className={inputClasses}
                  />
                </div>
              </div>
            </div>

            <div className="mt-[30px] flex flex-col">
              <h3 className="flex items-center !text-[1.125em] !font-semibold text-[#5882c1] pb-2">
                <div className="flex-shrink-0 w-8 mr-2 flex items-center justify-center grayscale-[0.5]">
                  <img src={inspectionTypesIcon} alt={intl.formatMessage({ id: 'PERMISSIONS' })} className="h-6" />
                </div>

                {intl.formatMessage({ id: 'PERMISSIONS' })}
              </h3>

              <div className="flex flex-wrap justify-between px-10">
                {permissions.length ? (
                  permissions.map(permission => (
                    <div
                      className="flex items-center w-full my-[0.75em] leading-[1.2] !text-[0.875em] text-[#393939] !font-medium"
                      key={permission?.id}
                    >
                      <input
                        type="checkbox"
                        name="permission"
                        value={permission?.id}
                        onChange={handleSelectedPermissionsChange}
                        checked={!!permission?.selected}
                        className="!mr-2 h-4 w-4 rounded border-gray-300 text-[#5882c1] focus:ring-[#5882c1]"
                      />

                      {permission?.description}
                    </div>
                  ))
                ) : (
                  <div className="w-full text-center text-[#919191] text-[0.875rem] py-4">
                    {intl.formatMessage({ id: 'NOT_FOUND_MESSAGE' })}
                  </div>
                )}
              </div>
            </div>

            <div className="flex justify-center gap-[10px] mt-[2em]">
              <Link
                to="../"
                className="flex items-center justify-center w-[100px] h-9 border border-[#e3e3e3] rounded-xm !text-[#3c3c3c] font-bold hover:bg-[#f8f8f8] transition-colors !no-underline text-sm"
              >
                {intl.formatMessage({ id: 'CANCEL' })}
              </Link>

              {!!permissions.length && (
                <GrayButton
                  onClick={handleClientUserFormSubmit}
                  isBusy={isClientUserFormSubmitting}
                  className="!w-[100px] !h-9 !border !border-[#e3e3e3] !rounded-xm !text-[#3c3c3c] font-bold !bg-white hover:bg-[#f8f8f8] transition-colors !no-underline text-sm"
                >
                  {intl.formatMessage({ id: 'SAVE' })}
                </GrayButton>
              )}
            </div>
          </Card>
        </>
      )}
    </div>
  );
};

UpdateUserPermissionsComponent.propTypes = {
  isBusy: PropTypes.bool.isRequired,
  permissions: PropTypes.array,
  isClientUserFormSubmitting: PropTypes.bool.isRequired,
  isClientUserDeleteConfirmationVisible: PropTypes.bool.isRequired,
  handleSelectedPermissionsChange: PropTypes.func.isRequired,
  handleClientUserDeleteButtonClick: PropTypes.func.isRequired,
  handleClientUserFormSubmit: PropTypes.func.isRequired,
  handleToggleClientUserDeleteConfirmation: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
  intl: PropTypes.object.isRequired,
  user: PropTypes.object.isRequired,
  cpfMask: PropTypes.array.isRequired,
  cpfMaskPlaceholder: PropTypes.string.isRequired,
  phoneMask: PropTypes.array.isRequired,
  phoneMaskPlaceholder: PropTypes.string.isRequired,
  cellphoneMask: PropTypes.array.isRequired,
  cellphoneMaskLength: PropTypes.number.isRequired,
};

UpdateUserPermissionsComponent.defaultProps = {
  permissions: [],
};

export default UpdateUserPermissionsComponent;
